import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { ChartData } from 'chart.js';
import './AdminPage.css';
import { useAdminPulse } from '../hooks/useAdminPulse';
import { useAdminActivity } from '../hooks/useAdminActivity';
import { useAdminUserGameSeries, UserGameDailyPoint } from '../hooks/useAdminUserGameSeries';
import { useAdminTodayPuzzle } from '../hooks/useAdminTodayPuzzle';
import LineChart, { MEDIAN_SOLVE_LABEL } from './LineChart';
import TodayPuzzleTable from './TodayPuzzleTable';

const STATS_DAYS = 30;

function formatDelta(
  today: number,
  yesterday: number | null | undefined
): { text: string; className: string } | null {
  if (yesterday == null) return null;
  const diff = today - yesterday;
  if (diff === 0) return { text: '±0 vs yesterday', className: 'delta-flat' };
  if (diff > 0) return { text: `+${diff} vs yesterday`, className: 'delta-positive' };
  return { text: `${diff} vs yesterday`, className: 'delta-negative' };
}

function buildModeChartData(
  points: UserGameDailyPoint[],
  palette: 'classic' | 'casual'
): ChartData<'line'> {
  const colors =
    palette === 'classic'
      ? {
          starts: '#4B5563',
          wins: '#6B7280',
          medianTime: '#374151',
        }
      : {
          starts: '#3B82F6',
          wins: '#60A5FA',
          medianTime: '#1D4ED8',
        };

  const lineDefaults = {
    backgroundColor: 'transparent',
    tension: 0.2,
    fill: false,
  };

  return {
    labels: points.map((p) => p.date),
    datasets: [
      {
        label: 'Starts',
        data: points.map((p) => p.starts),
        borderColor: colors.starts,
        yAxisID: 'y',
        ...lineDefaults,
      },
      {
        label: 'Wins',
        data: points.map((p) => p.wins),
        borderColor: colors.wins,
        yAxisID: 'y',
        ...lineDefaults,
      },
      {
        label: MEDIAN_SOLVE_LABEL,
        data: points.map((p) =>
          p.medianElapsedTimeMs == null ? null : Math.round(p.medianElapsedTimeMs / 1000)
        ),
        borderColor: colors.medianTime,
        borderDash: [6, 4],
        yAxisID: 'y1',
        ...lineDefaults,
      },
    ],
  };
}

function StatCard({
  label,
  value,
  subtitle,
  delta,
}: {
  label: string;
  value: string | number;
  subtitle?: string;
  delta?: { text: string; className: string } | null;
}) {
  return (
    <div className="stat-card">
      <h3>{label}</h3>
      <div className="stat-value">{value}</div>
      {delta && <p className={`stat-delta ${delta.className}`}>{delta.text}</p>}
      {subtitle && <p className="stat-subtitle">{subtitle}</p>}
    </div>
  );
}

export default function AdminPage() {
  const { data: pulseData, loading: pulseLoading, error: pulseError } = useAdminPulse();
  const { data: activityData, loading: activityLoading, error: activityError } = useAdminActivity();
  const { data: seriesData, loading: seriesLoading, error: seriesError } =
    useAdminUserGameSeries(STATS_DAYS);
  const { data: todayData, loading: todayLoading, error: todayError } = useAdminTodayPuzzle();

  const loading = pulseLoading || activityLoading || seriesLoading || todayLoading;

  const classicPoints = useMemo(() => {
    if (!seriesData?.adminUserGameSeries.success) return [] as UserGameDailyPoint[];
    return seriesData.adminUserGameSeries.classic || [];
  }, [seriesData]);

  const casualPoints = useMemo(() => {
    if (!seriesData?.adminUserGameSeries.success) return [] as UserGameDailyPoint[];
    return seriesData.adminUserGameSeries.casual || [];
  }, [seriesData]);

  const classicChartData = useMemo(
    () => buildModeChartData(classicPoints, 'classic'),
    [classicPoints]
  );
  const casualChartData = useMemo(
    () => buildModeChartData(casualPoints, 'casual'),
    [casualPoints]
  );

  if (loading) {
    return (
      <div className="admin-page">
        <div className="loading">Loading…</div>
      </div>
    );
  }

  const pulse = pulseData?.adminPulse;
  const activity = activityData?.adminActivity;
  const todayPuzzle = todayData?.adminTodayPuzzle;

  return (
    <div className="admin-page">
      <header className="admin-header">
        <h1>Puzzli Admin</h1>
        <Link to="/" className="back-link">
          Play Puzzli
        </Link>
      </header>

      <section className="admin-section" aria-labelledby="pulse-heading">
        <h2 id="pulse-heading" className="admin-section-title">
          Pulse
        </h2>
        {pulseError || !pulse?.success ? (
          <p className="section-error">
            Could not load pulse metrics:{' '}
            {pulseError?.message ?? pulse?.errors?.join(', ') ?? 'Unknown error'}
          </p>
        ) : (
          <div className="stat-grid">
            <StatCard
              label="Today wins — Classic"
              value={pulse.classicTodayWins}
              delta={formatDelta(pulse.classicTodayWins, pulse.classicYesterdayWins)}
            />
            <StatCard
              label="Today wins — Casual"
              value={pulse.casualTodayWins}
              delta={formatDelta(pulse.casualTodayWins, pulse.casualYesterdayWins)}
            />
            <StatCard label="DAU" value={pulse.dau} subtitle="completed today" />
            <StatCard label="New players today" value={pulse.newPlayersToday} />
          </div>
        )}
      </section>

      <section className="admin-section" aria-labelledby="charts-heading">
        <h2 id="charts-heading" className="admin-section-title">
          Puzzle trends
        </h2>
        {seriesError || !seriesData?.adminUserGameSeries.success ? (
          <p className="section-error">
            Could not load puzzle charts:{' '}
            {seriesError?.message ??
              seriesData?.adminUserGameSeries.errors?.join(', ') ??
              'Unknown error'}
          </p>
        ) : (
          <div className="charts-grid">
            <div className="chart-panel">
              <h3 className="chart-panel-title">Classic</h3>
              <LineChart
                chartData={classicChartData}
                modeLabel="Classic"
                points={classicPoints}
                statsDays={STATS_DAYS}
              />
            </div>
            <div className="chart-panel">
              <h3 className="chart-panel-title">Casual</h3>
              <LineChart
                chartData={casualChartData}
                modeLabel="Casual"
                points={casualPoints}
                statsDays={STATS_DAYS}
              />
            </div>
          </div>
        )}
      </section>

      <section className="admin-section" aria-labelledby="activity-heading">
        <h2 id="activity-heading" className="admin-section-title">
          Activity
        </h2>
        {activityError || !activity?.success ? (
          <p className="section-error">
            Could not load activity metrics:{' '}
            {activityError?.message ?? activity?.errors?.join(', ') ?? 'Unknown error'}
          </p>
        ) : (
          <div className="stat-grid">
            <StatCard
              label="DoD retention %"
              value={activity.dodRetentionPct != null ? `${activity.dodRetentionPct}%` : '—'}
              subtitle="yesterday's players who returned today"
            />
            <StatCard label="Active streakers" value={activity.activeStreakers} />
            <StatCard
              label="Active users"
              value={activity.activeUsers}
              subtitle="won in last 3 days, >10 wins in a mode"
            />
            <StatCard
              label="Players (7 days)"
              value={activity.usersCompletedLast7Days}
              subtitle="distinct users who won at least once"
            />
          </div>
        )}
      </section>

      <section className="admin-section" aria-labelledby="recent-puzzles-heading">
        <h2 id="recent-puzzles-heading" className="admin-section-title">
          Last 2 weeks
        </h2>
        {todayError || !todayPuzzle?.success ? (
          <p className="section-error">
            Could not load recent puzzle metrics:{' '}
            {todayError?.message ?? todayPuzzle?.errors?.join(', ') ?? 'Unknown error'}
          </p>
        ) : (
          <TodayPuzzleTable classic={todayPuzzle.classic} casual={todayPuzzle.casual} />
        )}
      </section>

      <footer className="admin-footer">Dates use Eastern Time</footer>
    </div>
  );
}
